import { createContext, useState, useEffect, useContext, useMemo, useCallback, useRef, type PropsWithChildren } from 'react'
import { supabase } from '../lib/supabaseClient'
import { getUserProfile, createGuestProfile } from '../lib/user-service'
import { updateUserActivity } from '../lib/dashboard-service'
import type { Session, User } from '@supabase/supabase-js'
import type { UserProfile } from '../types/user'

export interface AuthContextType {
  session: Session | null
  user: User | null
  role: string | null
  userProfile: UserProfile | null
  needsProfileCompletion: boolean
  profileDoesNotExist: boolean
  isLoading: boolean
  // NEW: Add balance and setter
  chefcoins_balance: number | null;
  setChefcoinsBalance: (balance: number) => void;
  signOut: () => Promise<void>
  signInWithGoogle: () => Promise<void>
  refreshProfile: () => Promise<void>
  isGuest: () => boolean
  canCreateRecipes: () => boolean
  canUseAssistant: () => boolean
  canUsePremiumFeatures: () => boolean;
  getLlmModelForUser: (usePremium?: boolean) => Promise<string>;

}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const HEARTBEAT_MIN_INTERVAL_MS = 5 * 60 * 1000; // 5 min client-side throttle

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [role, setRole] = useState<string | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [needsProfileCompletion, setNeedsProfileCompletion] = useState(false)
  const [profileDoesNotExist, setProfileDoesNotExist] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  // NEW: State for ChefCoins
  const [chefcoins_balance, setChefcoinsBalance] = useState<number | null>(null);

  const isGuest = useCallback(() => role === 'guest', [role]);
  const canCreateRecipes = useCallback(() => role === 'admin' || role === 'reader', [role]);
  const canUseAssistant = useCallback(() => role === 'admin' || role === 'reader', [role]);

  const canUsePremiumFeatures = useCallback(() => {
    // Admins also get premium features for testing purposes
    return userProfile?.subscription === 'premium' || userProfile?.role === 'admin';
  }, [userProfile]);

  const getLlmModelForUser = useCallback(async (usePremium: boolean = false) => {
    const { modelService } = await import('../services/modelService');
    return await modelService.getTextGenerationModel(usePremium);
  }, []);

  const loadUserProfile = useCallback(async (userId: string): Promise<void> => {
    try {
      // This function now depends on getUserProfile fetching the new column
      const profile = await getUserProfile(userId);
      
      if (profile) {
        setUserProfile(profile);
        setRole(profile.role);
        // NEW: Set the balance from the fetched profile.
        setChefcoinsBalance(profile.chefcoins_balance ?? 0);
        
        const skipped = sessionStorage.getItem('skippedProfileCompletion') === 'true';
        const needsCompletion = (!profile.name || !profile.country) && !(profile.role === 'guest' && skipped);
        setNeedsProfileCompletion(needsCompletion);
      } else {
        const email = user?.email || '';
        await createGuestProfile(userId, email);
        
        const guestProfile: UserProfile = {
          id: userId,
          email,
          role: 'guest',
          name: null,
          country: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          subscription: 'standard',
          studio_uses_count: 0,
          ai_tokens_used: 0,
          last_active_at: null,
          chefcoins_balance: 100, // Default for new guests
          last_coin_reset_at: null
        };
        
        setUserProfile(guestProfile);
        setRole('guest');
        // NEW: Set client-side balance for new guests. DB default handles permanent storage.
        setChefcoinsBalance(100); 
        setNeedsProfileCompletion(true);
        setProfileDoesNotExist(false);
      }
    } catch {
      setProfileDoesNotExist(true);
    }
  }, [user]);

  const refreshProfile = useCallback(async () => {
    if (user) {
      await loadUserProfile(user.id)
    }
  }, [user, loadUserProfile])

  useEffect(() => {
    const handleAuthChange = async (session: Session | null) => {
      setIsLoading(true)
      const currentUser = session?.user ?? null;
      if (currentUser && currentUser.id !== user?.id) {
        setSession(session)
        setUser(currentUser)
        await loadUserProfile(currentUser.id)
      } else if (!currentUser && user) {
        setSession(null)
        setUser(null)
        setRole(null)
        setUserProfile(null);
        // NEW: Clear balance on sign out
        setChefcoinsBalance(null);
      } else {
        setSession(session)
      }
      setIsLoading(false)
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      handleAuthChange(session)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      handleAuthChange(session)
    })

    return () => subscription.unsubscribe()
  }, [user, loadUserProfile])

  // ── Activity heartbeat ───────────────────────────────────────────────
  // Keeps user_profiles.last_active_at fresh for daily / session-persisted
  // users. auth.users.last_sign_in_at only flips on a brand-new authentication,
  // so without this a user who stays logged in for weeks shows as "last seen
  // 1 month ago" on the admin dashboard.
  //
  // The DB-side update_user_activity() RPC is throttled (only writes when the
  // existing value is older than 1 hour), so calling this on load + every 30
  // minutes + on tab refocus is cheap. Last-fire time is mirrored in a ref so
  // back-to-back triggers (e.g. blur→focus within 30s) don't even hit the
  // network.
  const lastHeartbeatRef = useRef<number>(0);

  const pingActivity = useCallback(async (userId: string | null | undefined) => {
    if (!userId) return;
    const now = Date.now();
    if (now - lastHeartbeatRef.current < HEARTBEAT_MIN_INTERVAL_MS) return;
    lastHeartbeatRef.current = now;
    await updateUserActivity(userId);
  }, []);

  // Fire once on login / user change.
  useEffect(() => {
    if (user?.id) {
      lastHeartbeatRef.current = Date.now();
      void updateUserActivity(user.id);
    } else {
      lastHeartbeatRef.current = 0;
    }
  }, [user?.id]);

  // Recurring ping every 30 minutes while the tab is open.
  useEffect(() => {
    if (!user?.id) return;
    const intervalMs = 30 * 60 * 1000;
    const id = window.setInterval(() => {
      void pingActivity(user.id);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [user?.id, pingActivity]);

  // Ping again when the tab becomes visible after being hidden (e.g. user
  // switched away and came back hours later).
  useEffect(() => {
    if (!user?.id) return;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void pingActivity(user.id);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [user?.id, pingActivity]);

  const signOut = useCallback(async () => {
    sessionStorage.removeItem('skippedProfileCompletion');
    await supabase.auth.signOut()
  }, [])

  const signInWithGoogle = useCallback(async () => {
    await supabase.auth.signInWithOAuth({ provider: 'google', options: { queryParams: { prompt: 'select_account' } } })
  }, [])


  const value = useMemo(() => ({
    session,
    user,
    role,
    userProfile,
    needsProfileCompletion,
    profileDoesNotExist,
    isLoading,
    // NEW: Add balance and setter to context value
    chefcoins_balance,
    setChefcoinsBalance,
    signOut,
    signInWithGoogle,
    refreshProfile,
    isGuest,
    canCreateRecipes,
    canUseAssistant,
    canUsePremiumFeatures,
    getLlmModelForUser
  }), [session, user, role, userProfile, needsProfileCompletion, profileDoesNotExist, isLoading, signOut, signInWithGoogle, refreshProfile, canUsePremiumFeatures, getLlmModelForUser, chefcoins_balance, isGuest, canCreateRecipes, canUseAssistant]) // NEW: Add chefcoins_balance to dependency array

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}